import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Editor from '@monaco-editor/react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useMobile, useTouch, useVibration } from '../hooks/useMobile';

const { FiZoomIn, FiZoomOut, FiMaximize, FiMinimize, FiKeyboard, FiEye, FiEyeOff } = FiIcons;

const TouchOptimizedEditor = ({ value, onChange, language, theme = 'vs-dark', fontSize: initialFontSize = 14, onFontSizeChange }) => {
  const { isMobile, isTablet, orientation } = useMobile();
  const isTouch = useTouch();
  const { vibrateTouch } = useVibration();

  const [fontSize, setFontSize] = useState(initialFontSize);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 });
  const [showMobileKeyboard, setShowMobileKeyboard] = useState(false);

  const editorRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-hide toolbar after inactivity on mobile
  useEffect(() => {
    if (!isMobile) return;

    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      setShowToolbar(true);
      timer = setTimeout(() => {
        setShowToolbar(false);
      }, 3000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', resetTimer);
      container.addEventListener('scroll', resetTimer);
      resetTimer();

      return () => {
        container.removeEventListener('touchstart', resetTimer);
        container.removeEventListener('scroll', resetTimer);
        clearTimeout(timer);
      };
    }
  }, [isMobile]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Touch-optimized settings
    if (isTouch) {
      editor.updateOptions({
        // Increase touch targets
        lineHeight: Math.max(fontSize * 1.4, 20),
        // Better scrolling on touch devices
        scrollBeyondLastLine: false,
        smoothScrolling: true,
        // Touch-friendly selection
        selectOnLineNumbers: true,
        // Auto-closing brackets
        autoClosingBrackets: 'always',
        autoClosingQuotes: 'always',
        // Better word wrapping
        wordWrap: 'on',
        wordWrapColumn: 80,
        // Touch-optimized minimap
        minimap: { enabled: !isMobile },
        // Larger scrollbars on mobile
        scrollbar: {
          vertical: isMobile ? 'visible' : 'auto',
          horizontal: isMobile ? 'visible' : 'auto',
          verticalScrollbarSize: isMobile ? 20 : 14,
          horizontalScrollbarSize: isMobile ? 20 : 14
        }
      });
    }

    // Track cursor position
    editor.onDidChangeCursorPosition((e) => {
      setCursorPosition({
        line: e.position.lineNumber,
        column: e.position.column
      });
    });

    // Handle mobile keyboard
    editor.onDidFocusEditorText(() => {
      if (isMobile) {
        setShowMobileKeyboard(true);
      }
    });

    editor.onDidBlurEditorText(() => {
      if (isMobile) {
        setShowMobileKeyboard(false);
      }
    });
  };

  const adjustFontSize = (delta) => {
    vibrateTouch();
    const newSize = Math.max(10, Math.min(32, fontSize + delta));
    setFontSize(newSize);
    onFontSizeChange?.(newSize);

    if (editorRef.current) {
      editorRef.current.updateOptions({
        fontSize: newSize,
        lineHeight: Math.max(newSize * 1.4, 20)
      });
    }
  };

  const toggleFullscreen = () => {
    vibrateTouch();
    setIsFullscreen(!isFullscreen);
  };

  const insertSnippet = (snippet) => {
    vibrateTouch();
    if (editorRef.current) {
      const position = editorRef.current.getPosition();
      editorRef.current.executeEdits('', [{
        range: {
          startLineNumber: position.lineNumber,
          startColumn: position.column,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        },
        text: snippet
      }]);
      editorRef.current.focus();
    }
  };

  const mobileSnippets = [
    { label: 'div', code: '<div></div>' },
    { label: 'class', code: 'className=""' },
    { label: 'func', code: 'function() {\n  \n}' },
    { label: 'const', code: 'const = ' },
    { label: 'log', code: 'console.log()' },
    { label: '=>', code: '=> ' },
    { label: '{}', code: '{}' },
    { label: '[]', code: '[]' }
  ];

  return (
    <div
      ref={containerRef}
      className={`relative h-full ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : ''}`}
    >
      {/* Touch Toolbar - REDUCED z-index to not interfere with main menu */}
      {(isMobile || isTablet) && (
        <motion.div
          className={`absolute top-0 left-0 right-0 z-[100] bg-gray-900/95 backdrop-blur-sm border-b border-gray-700 transition-all duration-300 ${
            showToolbar ? 'translate-y-0' : '-translate-y-full'
          }`}
          initial={{ y: -100 }}
          animate={{ y: showToolbar ? 0 : -100 }}
        >
          <div className="flex items-center justify-between p-2">
            {/* Font Size Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => adjustFontSize(-2)}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiZoomOut} className="text-gray-300" />
              </button>
              <span className="text-gray-300 text-sm font-medium min-w-[40px] text-center">
                {fontSize}px
              </span>
              <button
                onClick={() => adjustFontSize(2)}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiZoomIn} className="text-gray-300" />
              </button>
            </div>

            {/* Cursor Position */}
            <div className="text-xs text-gray-400">
              Ln {cursorPosition.line}, Col {cursorPosition.column}
            </div>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} className="text-gray-300" />
            </button>
          </div>

          {/* Mobile Code Snippets */}
          {isMobile && (
            <div className="flex gap-1 px-2 pb-2 overflow-x-auto">
              {mobileSnippets.map((snippet) => (
                <button
                  key={snippet.label}
                  onClick={() => insertSnippet(snippet.code)}
                  className="flex-shrink-0 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-gray-300 transition-colors"
                >
                  {snippet.label}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Editor */}
      <div className={`h-full ${(isMobile || isTablet) && showToolbar ? 'pt-16' : ''}`}>
        <Editor
          value={value}
          onChange={onChange}
          language={language}
          theme={theme}
          onMount={handleEditorDidMount}
          options={{
            fontSize,
            fontFamily: 'JetBrains Mono, Fira Code, Consolas, monospace',
            lineHeight: Math.max(fontSize * 1.4, 20),
            automaticLayout: true,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            minimap: { enabled: !isMobile },
            folding: !isMobile,
            lineNumbers: 'on',
            glyphMargin: !isMobile,
            // Touch optimizations
            scrollbar: {
              vertical: isMobile ? 'visible' : 'auto',
              horizontal: isMobile ? 'visible' : 'auto',
              verticalScrollbarSize: isMobile ? 20 : 14,
              horizontalScrollbarSize: isMobile ? 20 : 14
            },
            // Better touch selection
            selectOnLineNumbers: isTouch,
            // Auto-closing
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            // Smooth scrolling
            smoothScrolling: true,
            // Touch-friendly cursor
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: true,
            // Better rendering on mobile
            renderLineHighlight: isMobile ? 'gutter' : 'line',
            // Accessibility
            accessibilitySupport: 'on'
          }}
        />
      </div>

      {/* Mobile Keyboard Helper */}
      {isMobile && showMobileKeyboard && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-2 z-[101]"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
        >
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Tap to hide toolbar • Pinch to zoom</span>
            <button
              onClick={() => setShowToolbar(!showToolbar)}
              className="flex items-center gap-1 text-blue-400"
            >
              <SafeIcon icon={showToolbar ? FiEyeOff : FiEye} />
              Toolbar
            </button>
          </div>
        </motion.div>
      )}

      {/* Touch Gestures Overlay (for mobile) */}
      {isMobile && (
        <div className="absolute inset-0 pointer-events-none">
          {/* Visual indicators for gestures could go here */}
        </div>
      )}
    </div>
  );
};

export default TouchOptimizedEditor;